import { useState, useRef, useEffect } from "react";
import { RefreshCcw, Phone, MessageCircle, Send, CheckCircle } from "lucide-react";

const INITIAL = { name: "", phone: "", rxNumber: "", pickup: "in-store", notes: "" };

export default function RefillForm() {
  const [form, setForm] = useState(INITIAL);
  const [submitted, setSubmitted] = useState(false);
  const [method, setMethod] = useState("whatsapp");
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.15 }
    );
    const items = ref.current?.querySelectorAll(".animate-on-view");
    items?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const buildWhatsApp = () => {
    const msg = `Hi EZ Care Pharmacy! I'd like to request a refill.%0A%0A*Name:* ${form.name}%0A*Phone:* ${form.phone}%0A*Rx Number:* ${form.rxNumber || "N/A"}%0A*Pickup:* ${form.pickup}%0A*Notes:* ${form.notes || "None"}`;
    return `https://wa.me/12016742138?text=${msg}`;
  };

  const buildPhone = () => `tel:+18625201063`;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;

    if (method === "whatsapp") {
      window.open(buildWhatsApp(), "_blank");
    } else {
      window.location.href = buildPhone();
    }

    setSubmitted(true);
    setTimeout(() => { setSubmitted(false); setForm(INITIAL); }, 3000);
  };

  return (
    <section
      id="refill"
      data-testid="refill-section"
      className="ez-section bg-gradient-to-br from-ez-blue-light to-teal-50"
      ref={ref}
    >
      <div className="ez-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* LEFT: Info */}
          <div className="animate-on-view">
            <div className="inline-block text-ez-teal font-semibold font-caveat text-lg mb-3">
              Quick &amp; Easy
            </div>
            <h2 className="font-outfit text-2xl md:text-3xl font-bold text-ez-text mb-6 leading-tight">
              Request a
              <br />
              <span className="text-ez-teal">Prescription Refill</span>
            </h2>
            <p className="text-ez-muted text-base leading-relaxed mb-8 font-jakarta">
              Fill out the form and reach us instantly via WhatsApp or give us a call.
              We'll have your prescription ready for pickup or delivery.
            </p>

            <div className="space-y-4">
              {[
                { icon: RefreshCcw, title: "Fast Processing", desc: "Most refills ready within hours" },
                { icon: Phone, title: "Call Us Directly", desc: "(862) 520-1063 — Mon to Sat" },
                { icon: MessageCircle, title: "WhatsApp Us", desc: "Instant response via WhatsApp" },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon size={18} className="text-ez-teal" />
                  </div>
                  <div>
                    <div className="font-semibold text-ez-text text-sm font-outfit">{title}</div>
                    <div className="text-ez-muted text-sm font-jakarta">{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-amber-800 text-sm font-jakarta">
                <strong>Important Notice:</strong> This form does not collect or store any medical information.
                Your details are sent directly to our pharmacy via WhatsApp or phone.
              </p>
            </div>
          </div>

          {/* RIGHT: Form */}
          <div className="animate-on-view" style={{ transitionDelay: "0.2s" }}>
            <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-ez-teal" />
                  </div>
                  <h3 className="font-outfit font-bold text-2xl text-ez-text mb-2">Request Sent!</h3>
                  <p className="text-ez-muted font-jakarta">We'll get back to you shortly.</p>
                </div>
              ) : (
                <>
                  <h3 className="font-outfit font-bold text-2xl text-ez-text mb-6">Refill Request</h3>

                  {/* Method toggle */}
                  <div className="flex gap-2 p-1 bg-gray-100 rounded-xl mb-6">
                    <button
                      type="button"
                      data-testid="method-whatsapp"
                      onClick={() => setMethod("whatsapp")}
                      className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                        method === "whatsapp"
                          ? "bg-white shadow text-green-600"
                          : "text-ez-muted"
                      }`}
                    >
                      <MessageCircle size={15} />
                      WhatsApp
                    </button>
                    <button
                      type="button"
                      data-testid="method-call"
                      onClick={() => setMethod("call")}
                      className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                        method === "call"
                          ? "bg-white shadow text-ez-teal"
                          : "text-ez-muted"
                      }`}
                    >
                      <Phone size={15} />
                      Call Us
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-ez-text mb-1.5 font-outfit">
                          Full Name *
                        </label>
                        <input
                          data-testid="refill-name"
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          required
                          placeholder="Your name"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ez-teal/30 focus:border-ez-teal transition-all font-jakarta"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-ez-text mb-1.5 font-outfit">
                          Phone Number *
                        </label>
                        <input
                          data-testid="refill-phone"
                          type="tel"
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          required
                          placeholder="(862) 555-0000"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ez-teal/30 focus:border-ez-teal transition-all font-jakarta"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-ez-text mb-1.5 font-outfit">
                          Rx Number
                        </label>
                        <input
                          data-testid="refill-rx"
                          type="text"
                          name="rxNumber"
                          value={form.rxNumber}
                          onChange={handleChange}
                          placeholder="Rx #12345"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ez-teal/30 focus:border-ez-teal transition-all font-jakarta"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-ez-text mb-1.5 font-outfit">
                          Pickup Method
                        </label>
                        <select
                          data-testid="refill-pickup"
                          name="pickup"
                          value={form.pickup}
                          onChange={handleChange}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ez-teal/30 focus:border-ez-teal transition-all font-jakarta bg-white"
                        >
                          <option value="in-store">In-Store Pickup</option>
                          <option value="delivery">Home Delivery</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-ez-text mb-1.5 font-outfit">
                        Additional Notes
                      </label>
                      <textarea
                        data-testid="refill-notes"
                        name="notes"
                        value={form.notes}
                        onChange={handleChange}
                        rows={3}
                        placeholder="Any specific requests or notes..."
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ez-teal/30 focus:border-ez-teal transition-all font-jakarta resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      data-testid="refill-submit"
                      className="w-full bg-ez-teal hover:bg-ez-teal-dark text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 font-outfit"
                    >
                      <Send size={18} />
                      {method === "whatsapp" ? "Send via WhatsApp" : "Call to Confirm"}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
